import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { supabase } from "@/lib/supabase";

/* ================================================================
   관리자 경매 분석 대시보드
   - 등급별 통계 / 지역별 분포 / 낙찰가율 트렌드 / 입찰 캘린더
================================================================ */

interface AuctionItem {
  id: string;
  case_number: string;
  location: string;
  court: string;
  property_type: string;
  appraised_value: number;
  minimum_price: number;
  auction_date: string;
  auction_time: string;
  failure_count: number;
  grade: string;
  winning_bid: number;
  winning_bid_ratio: number;
  has_tenant: boolean;
  rights_analysis: string;
  exclusive_area: number;
}

const GRADE_COLORS: Record<string, string> = { A: "#10B981", B: "#F59E0B", C: "#9CA3AF" };
const REGION_COLORS = ["#C4956A", "#10B981", "#F59E0B", "#6366F1", "#EF4444", "#8B5CF6", "#14B8A6"];

export default function AuctionAnalysisDashboard() {
  const [items, setItems] = useState<AuctionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase.from("kangstudy_auction_items").select("*");
        setItems(
          (data || []).map((i: Record<string, unknown>) => ({
            id: i.id as string,
            case_number: (i.case_number as string) || "",
            location: (i.location as string) || "",
            court: (i.court as string) || "",
            property_type: (i.property_type as string) || "아파트",
            appraised_value: (i.appraised_value as number) || 0,
            minimum_price: (i.minimum_price as number) || 0,
            auction_date: (i.auction_date as string) || "",
            auction_time: (i.auction_time as string) || "",
            failure_count: (i.failure_count as number) || 0,
            grade: (i.grade as string) || "C",
            winning_bid: (i.winning_bid as number) || 0,
            winning_bid_ratio: (i.winning_bid_ratio as number) || 0,
            has_tenant: !!(i.has_tenant),
            rights_analysis: (i.rights_analysis as string) || "",
            exclusive_area: (i.exclusive_area as number) || 0,
          }))
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#8C8780]">
        <i className="ri-loader-4-line animate-spin mr-2"></i>
        <span className="text-sm">분석 데이터 불러오는 중...</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-[#B8B3AE]">
        <i className="ri-pie-chart-2-line text-4xl mb-3"></i>
        <p className="text-sm font-medium">등록된 경매 물건이 없습니다</p>
        <p className="text-xs mt-1">엑셀 업로드 또는 물건 관리 탭에서 데이터를 먼저 입력해주세요</p>
      </div>
    );
  }

  // ── 통계 계산
  const total = items.length;
  const completed = items.filter((i) => i.winning_bid > 0);
  const upcoming = items.filter((i) => !i.winning_bid);
  const gradeA = items.filter((i) => i.grade === "A");
  const gradeB = items.filter((i) => i.grade === "B");
  const gradeC = items.filter((i) => i.grade === "C");

  const avgWinRatio =
    completed.length > 0
      ? Math.round(
          (completed.reduce((s, i) => {
            const ratio =
              i.winning_bid_ratio > 0
                ? i.winning_bid_ratio
                : i.appraised_value > 0
                ? Math.round((i.winning_bid / i.appraised_value) * 1000) / 10
                : 0;
            return s + ratio;
          }, 0) /
            completed.length) *
            10
        ) / 10
      : 0;

  const avgMinRatio =
    items.filter((i) => i.appraised_value > 0).length > 0
      ? Math.round(
          (items
            .filter((i) => i.appraised_value > 0)
            .reduce((s, i) => s + (i.minimum_price / i.appraised_value) * 100, 0) /
            items.filter((i) => i.appraised_value > 0).length) *
            10
        ) / 10
      : 0;

  // ── 등급별 파이차트 데이터
  const gradeData = [
    { name: "A등급", value: gradeA.length, color: "#10B981" },
    { name: "B등급", value: gradeB.length, color: "#F59E0B" },
    { name: "C등급", value: gradeC.length, color: "#9CA3AF" },
  ].filter((d) => d.value > 0);

  // ── 지역별 분포 (상위 8)
  const regionMap: Record<string, number> = {};
  items.forEach((i) => {
    const region = i.location.split(" ").slice(0, 2).join(" ") || "기타";
    regionMap[region] = (regionMap[region] || 0) + 1;
  });
  const regionData = Object.entries(regionMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  // ── 물건 종류별 낙찰가율
  const typeRatioMap: Record<string, number[]> = {};
  completed.forEach((i) => {
    const t = i.property_type;
    const r =
      i.winning_bid_ratio > 0
        ? i.winning_bid_ratio
        : i.appraised_value > 0
        ? Math.round((i.winning_bid / i.appraised_value) * 1000) / 10
        : 0;
    if (r > 0) {
      if (!typeRatioMap[t]) typeRatioMap[t] = [];
      typeRatioMap[t].push(r);
    }
  });
  const typeRatioData = Object.entries(typeRatioMap).map(([type, ratios]) => ({
    type,
    avg: Math.round((ratios.reduce((s, r) => s + r, 0) / ratios.length) * 10) / 10,
    count: ratios.length,
  }));

  // ── 이번 주 + 다음 주 입찰 예정
  const today = new Date();
  const twoWeeksLater = new Date(today);
  twoWeeksLater.setDate(today.getDate() + 14);
  const upcomingSchedule = upcoming
    .filter((i) => {
      if (!i.auction_date) return false;
      const d = new Date(i.auction_date);
      return d >= today && d <= twoWeeksLater;
    })
    .sort((a, b) => new Date(a.auction_date).getTime() - new Date(b.auction_date).getTime());

  // ── 등급별 평균 감정가
  const gradeAvgData = ["A", "B", "C"].map((g) => {
    const gItems = items.filter((i) => i.grade === g && i.appraised_value > 0);
    return {
      grade: `${g}등급`,
      avgAppraised: gItems.length > 0 ? Math.round(gItems.reduce((s, i) => s + i.appraised_value, 0) / gItems.length / 10000) : 0,
      avgMinimum: gItems.length > 0 ? Math.round(gItems.reduce((s, i) => s + i.minimum_price, 0) / gItems.length / 10000) : 0,
      count: gItems.length,
    };
  }).filter((d) => d.count > 0);

  const summaryCards = [
    { label: "전체 물건", value: `${total}건`, sub: `입찰예정 ${upcoming.length}건`, icon: "ri-building-line", color: "#C4956A" },
    { label: "A등급 물건", value: `${gradeA.length}건`, sub: `전체의 ${total > 0 ? Math.round((gradeA.length / total) * 100) : 0}%`, icon: "ri-star-line", color: "#10B981" },
    { label: "낙찰 완료", value: `${completed.length}건`, sub: `평균 낙찰가율 ${avgWinRatio || "—"}%`, icon: "ri-auction-line", color: "#F59E0B" },
    { label: "평균 최저가율", value: `${avgMinRatio}%`, sub: "감정가 대비 최저가", icon: "ri-percent-line", color: "#6366F1" },
  ];

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-[#1C1917]">분석 보고서</h3>
          <p className="text-xs text-[#8C8780] mt-0.5">등록된 경매 물건 {total}건 기준 · 실시간 데이터</p>
        </div>
        <span className="text-xs text-[#B8B3AE]">{new Date().toLocaleDateString("ko-KR")} 기준</span>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((s, i) => (
          <div key={i} className="bg-white border border-[#E8E5E0] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#8C8780]">{s.label}</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + "15" }}>
                <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
              </div>
            </div>
            <p className="text-xl font-bold text-[#1C1917]">{s.value}</p>
            <p className="text-xs text-[#8C8780] mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* 차트 row 1: 등급 파이 + 지역 바 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* 등급 분포 */}
        <div className="bg-white border border-[#E8E5E0] rounded-xl p-5">
          <h4 className="text-sm font-bold text-[#1C1917] mb-4">등급별 물건 분포</h4>
          {gradeData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={gradeData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" stroke="none">
                    {gradeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 flex-1">
                {gradeData.map((g) => (
                  <div key={g.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: g.color }}></div>
                      <span className="text-xs text-[#5C5650]">{g.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-[#F0EDE8] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(g.value / total) * 100}%`, backgroundColor: g.color }}></div>
                      </div>
                      <span className="text-xs font-bold text-[#1C1917] w-8 text-right">{g.value}건</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[#B8B3AE] text-center py-8">데이터 없음</p>
          )}
        </div>

        {/* 지역별 분포 */}
        <div className="bg-white border border-[#E8E5E0] rounded-xl p-5">
          <h4 className="text-sm font-bold text-[#1C1917] mb-4">지역별 물건 수</h4>
          {regionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={regionData} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: "#8C8780" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#5C5650" }} axisLine={false} tickLine={false} width={80} />
                <Tooltip formatter={(v) => [`${v}건`, "물건 수"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E8E5E0" }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {regionData.map((_, i) => <Cell key={i} fill={REGION_COLORS[i % REGION_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-[#B8B3AE] text-center py-8">데이터 없음</p>
          )}
        </div>
      </div>

      {/* 차트 row 2: 등급별 가격 비교 + 낙찰가율 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* 등급별 평균 감정가/최저가 */}
        <div className="bg-white border border-[#E8E5E0] rounded-xl p-5">
          <h4 className="text-sm font-bold text-[#1C1917] mb-4">등급별 평균 가격 비교 (만원)</h4>
          {gradeAvgData.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={gradeAvgData} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                <XAxis dataKey="grade" tick={{ fontSize: 11, fill: "#5C5650" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#8C8780" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`${(v as number).toLocaleString()}만원`]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E8E5E0" }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="avgAppraised" name="평균 감정가" fill="#C4956A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="avgMinimum" name="평균 최저가" fill="#F0D9C5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-[#B8B3AE] text-center py-8">데이터 없음</p>
          )}
        </div>

        {/* 낙찰가율 분포 (완료된 물건 기준) */}
        <div className="bg-white border border-[#E8E5E0] rounded-xl p-5">
          <h4 className="text-sm font-bold text-[#1C1917] mb-1">물건 종류별 낙찰가율</h4>
          <p className="text-xs text-[#8C8780] mb-4">낙찰 완료 {completed.length}건 기준</p>
          {typeRatioData.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={typeRatioData} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                <XAxis dataKey="type" tick={{ fontSize: 11, fill: "#5C5650" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#8C8780" }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip formatter={(v) => [`${v}%`, "평균 낙찰가율"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E8E5E0" }} />
                <Bar dataKey="avg" name="낙찰가율" radius={[4, 4, 0, 0]}>
                  {typeRatioData.map((entry, i) => (
                    <Cell key={i} fill={entry.avg >= 90 ? "#EF4444" : entry.avg >= 80 ? "#F59E0B" : "#10B981"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-[#B8B3AE]">
              <i className="ri-bar-chart-line text-2xl mb-2"></i>
              <p className="text-xs">낙찰 완료 데이터가 없습니다</p>
              <p className="text-[10px] mt-1">물건 관리 탭에서 낙찰가를 입력해주세요</p>
            </div>
          )}
        </div>
      </div>

      {/* 이번 주/다음 주 입찰 캘린더 */}
      <div className="bg-white border border-[#E8E5E0] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold text-[#1C1917]">향후 2주 입찰 예정</h4>
            <p className="text-xs text-[#8C8780] mt-0.5">{upcomingSchedule.length}건</p>
          </div>
        </div>

        {upcomingSchedule.length === 0 ? (
          <p className="text-xs text-[#B8B3AE] text-center py-8">향후 2주간 입찰 예정 물건이 없습니다</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#F0EDE8] text-left">
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">입찰일</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">시간</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">소재지</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">종류</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4 text-right">최저가</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">등급</th>
                  <th className="pb-2 font-semibold text-[#8C8780]">유찰</th>
                </tr>
              </thead>
              <tbody>
                {upcomingSchedule.map((item) => {
                  const daysLeft = Math.ceil(
                    (new Date(item.auction_date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
                  );
                  return (
                    <tr key={item.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8]">
                      <td className="py-2 pr-4">
                        <div>
                          <span className="font-medium text-[#1C1917]">{item.auction_date}</span>
                          {daysLeft <= 3 && (
                            <span className="ml-1.5 text-[10px] bg-red-100 text-red-600 px-1 py-0.5 rounded">D-{daysLeft}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-[#8C8780]">{item.auction_time || "—"}</td>
                      <td className="py-2 pr-4 text-[#5C5650] max-w-[140px] truncate">{item.location}</td>
                      <td className="py-2 pr-4">
                        <span className="px-1.5 py-0.5 rounded bg-[#F5F3F0] text-[#5C5650]">{item.property_type}</span>
                      </td>
                      <td className="py-2 pr-4 font-medium text-[#1C1917] text-right">
                        {item.minimum_price > 0 ? `${Math.round(item.minimum_price / 10000).toLocaleString()}만` : "—"}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-bold ${GRADE_COLORS[item.grade] ? "" : ""}`}
                          style={{ backgroundColor: (GRADE_COLORS[item.grade] || "#9CA3AF") + "20", color: GRADE_COLORS[item.grade] || "#9CA3AF" }}>
                          {item.grade}
                        </span>
                      </td>
                      <td className="py-2 text-[#8C8780]">{item.failure_count}회</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 낙찰 완료 물건 목록 */}
      {completed.length > 0 && (
        <div className="bg-white border border-[#E8E5E0] rounded-xl p-5">
          <h4 className="text-sm font-bold text-[#1C1917] mb-4">낙찰 완료 물건 ({completed.length}건)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#F0EDE8] text-left">
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">소재지</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">종류</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4 text-right">감정가</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4 text-right">낙찰가</th>
                  <th className="pb-2 font-semibold text-[#8C8780] pr-4">낙찰가율</th>
                  <th className="pb-2 font-semibold text-[#8C8780]">등급</th>
                </tr>
              </thead>
              <tbody>
                {completed.map((item) => {
                  const ratio =
                    item.winning_bid_ratio > 0
                      ? item.winning_bid_ratio
                      : item.appraised_value > 0
                      ? Math.round((item.winning_bid / item.appraised_value) * 1000) / 10
                      : 0;
                  return (
                    <tr key={item.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8]">
                      <td className="py-2 pr-4 text-[#5C5650] max-w-[140px] truncate">{item.location}</td>
                      <td className="py-2 pr-4"><span className="px-1.5 py-0.5 rounded bg-[#F5F3F0] text-[#5C5650]">{item.property_type}</span></td>
                      <td className="py-2 pr-4 text-right text-[#8C8780]">{item.appraised_value > 0 ? `${Math.round(item.appraised_value / 10000).toLocaleString()}만` : "—"}</td>
                      <td className="py-2 pr-4 text-right font-bold text-[#1C1917]">{Math.round(item.winning_bid / 10000).toLocaleString()}만</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${ratio >= 90 ? "bg-red-100 text-red-600" : ratio >= 80 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}>
                          {ratio}%
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="text-[11px] font-bold" style={{ color: GRADE_COLORS[item.grade] || "#9CA3AF" }}>{item.grade}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}